import React, { useContext, useEffect, useMemo, useState } from 'react';

import { Formik } from 'formik';

import ProjectResourcesTable from './ProjectResourcesTable';

import Button from 'CommonComponents/Button';
import TableSearchInput from 'CommonComponents/TableSearchInput';

import ProjectPlanContext from 'Context/project_plan/ProjectPlanContext';

import useApiRequest from 'Hooks/useApiRequest';

const filterResourcesBySearch = (resources, search) => {
    const lowercasedSearch = search.toLowerCase();

    return resources
        .map((dept) => {
            const filteredPositions = dept.positions?.filter((pos) => pos.name.toLowerCase().includes(lowercasedSearch)) || [];
            const filteredCostcenters =
                dept.costcenters
                    ?.map((cc) => {
                        const filteredCCPositions = cc.positions?.filter((pos) => pos.name.toLowerCase().includes(lowercasedSearch)) || [];
                        if (filteredCCPositions.length > 0) {
                            return { ...cc, positions: filteredCCPositions };
                        }
                        return null;
                    })
                    .filter((cc) => cc !== null) || [];

            if (filteredPositions.length > 0 || filteredCostcenters.length > 0) {
                return {
                    ...dept,
                    positions: filteredPositions,
                    costcenters: filteredCostcenters,
                };
            }
            return null;
        })
        .filter((dept) => dept !== null);
};

const ResourceAllocationTab = ({ activity, onCancel }) => {
    const [taggingMode, setTaggingMode] = useState(false);
    const [search, setSearch] = useState('');

    const { resources: resourcesRequest, allocateProjectResources, ganttChart, fetchProjectActivityResourcesUrl } = useContext(ProjectPlanContext);

    const activityResourcesRequest = useApiRequest({
        url: fetchProjectActivityResourcesUrl,
    });

    const resources = useMemo(() => {
        const resourcesData = resourcesRequest?.data || [];
        if (!search) {
            return resourcesData;
        } else {
            return filterResourcesBySearch(resourcesData, search);
        }
    }, [resourcesRequest.data, search]);

    useEffect(() => {
        if (activity?.id && !taggingMode) {
            activityResourcesRequest.get(null, { ACTIVITY_ID: activity.id });
        }
    }, [activity, taggingMode]);

    const allocatedResources = useMemo(() => {
        return activityResourcesRequest.data ? activityResourcesRequest.data.data : [];
    }, [activityResourcesRequest.data]);

    const initialValues = useMemo(() => {
        const selected = {};
        if (resources && resources.length > 0) {
            resources.forEach((dept) => {
                dept.positions?.forEach((pos) => {
                    selected[pos.id] = false;
                });
                dept.costcenters?.forEach((cc) => {
                    cc.positions?.forEach((pos) => {
                        selected[pos.id] = false;
                    });
                });
            });
        }

        allocatedResources.forEach((dept) => {
            dept.positions?.forEach((pos) => {
                selected[pos.id] = true;
            });
            dept.costcenters?.forEach((cc) => {
                cc.positions?.forEach((pos) => {
                    selected[pos.id] = true;
                });
            });
        });

        return { selected };
    }, [resources, allocatedResources]);

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={(values, actions) => {
                const selectedResourceIds = Object.entries(values.selected)
                    .filter(([_, isSelected]) => isSelected)
                    .map(([id, _]) => id);

                allocateProjectResources({ resources: selectedResourceIds, activityId: activity.id })
                    .then(() => {
                        setTaggingMode(false);
                        ganttChart.refetch();
                    })
                    .finally(() => {
                        actions.setSubmitting(false);
                    });
            }}
        >
            {({ handleSubmit, isSubmitting }) => (
                // Fills the pop-up's tab body without growing it, so the table scrolls instead.
                <div className="tw-flex tw-flex-col tw-grow tw-basis-0 tw-min-h-0">
                    <div className="tw-flex tw-items-center tw-justify-between tw-pb-6">
                        <div className="tw-text-base tw-font-medium">{$t('Allocated Resources')}</div>
                        <div className="tw-flex tw-gap-x-1.5">
                            {taggingMode && (
                                <TableSearchInput
                                    searchValue={search}
                                    placeholder={$t('Search positions by name')}
                                    handleEnterPress={(e, value) => setSearch(value)}
                                    handleBlur={(e, value) => setSearch(value)}
                                />
                            )}
                            <Button variant="primary" onClick={() => setTaggingMode(true)} disabled={taggingMode}>
                                {$t('Tag Positions')}
                            </Button>
                        </div>
                    </div>
                    <ProjectResourcesTable
                        resources={taggingMode ? resources : allocatedResources}
                        loading={taggingMode ? resourcesRequest.loading : activityResourcesRequest.loading}
                        editable={taggingMode}
                    />
                    {/* One Cancel, two meanings: while tagging it backs out of tagging, and
                        otherwise it closes the modal — the same thing Cancel does on the Edit
                        Activity tab. The footer is always rendered so the tab is never left
                        without a way out, but the two Cancels never appear together. */}
                    <div className="modal__footer tw-mt-auto tw-pt-7">
                        <Button variant="secondary" onClick={taggingMode ? () => setTaggingMode(false) : onCancel} disabled={isSubmitting}>
                            {$t('Cancel')}
                        </Button>
                        {taggingMode && (
                            <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
                                {$t('Save')}
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </Formik>
    );
};

export default ResourceAllocationTab;
